import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import ResetPasswordModal from '../../src/components/ResetPasswordModal';
import StageBadge from '../../src/components/StageBadge';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import { formatDateTime } from '../../src/utils/timezone';

interface OwnerItem {
  id: string; accountNumber: string; email: string; businessName: string;
  phone: string | null; stripeOnboarded: boolean; isSuspended: boolean; createdAt: string;
  lastActiveAt: string | null;
  // Acquisition milestones (JSON list only — not in the CSV export)
  hasLocation: boolean; hasController: boolean; activated: boolean; firstRevenueAt: string | null;
}

/** AdminOwners - list, search, suspend, and manage business owner accounts. */
export default function AdminOwners() {
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const router = useRouter();
  const [owners, setOwners] = useState<OwnerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createBusiness, setCreateBusiness] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [creating, setCreating] = useState(false);
  // Reset password modal
  const [resetTarget, setResetTarget] = useState<OwnerItem | null>(null);
  // Inline confirm / message
  const [confirmSuspend, setConfirmSuspend] = useState<string | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchOwners = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      const data = await api<{ owners: OwnerItem[]; total: number }>(`/api/admin/owners?${params}`);
      if (signal?.aborted) return;
      setOwners(data.owners);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchOwners(1, '', ac.signal); return () => ac.abort(); }, []);

  const handleSearch = () => fetchOwners(1, search);
  const handlePage = (p: number) => fetchOwners(p, search);

  const handleSuspendToggle = (ownerId: string) => {
    setConfirmSuspend(ownerId);
  };

  const confirmSuspendToggle = async () => {
    if (!confirmSuspend) return;
    const target = owners.find(o => o.id === confirmSuspend);
    const action = target?.isSuspended ? 'unsuspend' : 'suspend';
    setSuspending(true);
    try {
      await api(`/api/admin/owners/${confirmSuspend}/${action}`, { method: 'POST', body: {} });
      setConfirmSuspend(null);
      fetchOwners(page, search);
    } catch (err: any) {
      setConfirmSuspend(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setSuspending(false);
    }
  };

  const handleCreate = async () => {
    if (!createEmail || !createPassword || !createBusiness) {
      setInlineMsg({ type: 'error', message: 'Email, password, and business name are required' });
      return;
    }
    setCreating(true);
    try {
      await api('/api/admin/owners', {
        method: 'POST',
        body: { email: createEmail, password: createPassword, businessName: createBusiness, phone: createPhone || undefined },
      });
      setShowCreate(false);
      setCreateEmail(''); setCreatePassword(''); setCreateBusiness(''); setCreatePhone('');
      fetchOwners(1, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setCreating(false); }
  };

  const handleResetPassword = async (password: string) => {
    if (!resetTarget) return;
    await api(`/api/admin/owners/${resetTarget.id}/reset-password`, {
      method: 'POST',
      body: { password },
    });
  };

  const csvEndpoint = `/api/admin/owners?${new URLSearchParams({ ...(search && { search }) })}`;

  const renderItem = ({ item }: { item: OwnerItem }) => (
    <View>
      <TouchableOpacity style={styles.row} onPress={() => router.push(`/admin/owner/${item.id}` as never)}>
        <View style={[styles.avatar, { backgroundColor: '#f0e4db' }]}>
          <Ionicons name="business" size={18} color="#733e24" />
        </View>
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <Text style={styles.name}>{item.businessName}</Text>
            {item.isSuspended && (
              <View style={[styles.badge, { backgroundColor: '#dc262615' }]}>
                <Text style={[styles.badgeText, { color: '#dc2626' }]}>Suspended</Text>
              </View>
            )}
            <View style={[styles.badge, { backgroundColor: item.stripeOnboarded ? '#05966915' : '#d9770615' }]}>
              <Text style={[styles.badgeText, { color: item.stripeOnboarded ? '#059669' : '#d97706' }]}>
                {item.stripeOnboarded ? 'Stripe ✓' : 'Stripe ⏳'}
              </Text>
            </View>
          </View>
          <Text style={styles.meta}>{item.email} · {item.accountNumber}{item.phone ? ` · ${item.phone}` : ''}</Text>
          <Text style={styles.date}>Joined {new Date(item.createdAt).toLocaleDateString()} · Last active {item.lastActiveAt ? formatDateTime(item.lastActiveAt) : 'Never'}</Text>
          {/* Acquisition milestones — mirrors admin/acquisition.tsx funnel chips */}
          <View style={styles.stageRow}>
            <StageBadge label="Stripe" on={item.stripeOnboarded} />
            <StageBadge label="Location" on={item.hasLocation} />
            <StageBadge label="Controller" on={item.hasController} />
            <StageBadge label="Activated" on={item.activated} />
            <StageBadge label="Revenue" on={!!item.firstRevenueAt} />
          </View>
        </View>
        <View style={styles.rowActions}>
          <TouchableOpacity onPress={() => setResetTarget(item)} hitSlop={8}>
            <Ionicons name="key-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleSuspendToggle(item.id)} hitSlop={8}>
            <Ionicons name={item.isSuspended ? 'play-circle-outline' : 'pause-circle-outline'} size={18} color={item.isSuspended ? colors.primary : colors.danger} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
      <InlineConfirm
        message={`${item.isSuspended ? 'Unsuspend' : 'Suspend'} ${item.businessName}?`}
        visible={confirmSuspend === item.id}
        onConfirm={confirmSuspendToggle}
        onCancel={() => setConfirmSuspend(null)}
        confirmLabel={item.isSuspended ? 'Unsuspend' : 'Suspend'}
        loading={suspending}
        variant={item.isSuspended ? undefined : 'danger'}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      {/* Create button */}
      <TouchableOpacity style={styles.createBtn} onPress={() => setShowCreate(true)}>
        <Ionicons name="add" size={20} color={colors.white} />
        <Text style={styles.createText}>Create Owner</Text>
      </TouchableOpacity>

      <InlineMessage
        type={inlineMsg?.type || 'error'}
        message={inlineMsg?.message || ''}
        visible={!!inlineMsg}
        onDismiss={() => setInlineMsg(null)}
        autoDismissMs={4000}
      />

      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={handleSearch}
        total={total} page={page} limit={20} onPageChange={handlePage}
        csvEndpoint={csvEndpoint} csvFilename="owners.csv" label="owner"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />}
          data={owners}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No owners found</Text>}
        />
      )}

      {/* Create Owner Modal */}
      <Modal visible={showCreate} transparent animationType="slide" onRequestClose={() => setShowCreate(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Create Owner</Text>
            <ScrollView nestedScrollEnabled>
              <Text style={styles.fieldLabel}>Business Name *</Text>
              <TextInput style={styles.fieldInput} placeholder="Business name" placeholderTextColor={colors.textLight}
                value={createBusiness} onChangeText={setCreateBusiness} maxLength={100} />
              <Text style={styles.fieldLabel}>Email *</Text>
              <TextInput style={styles.fieldInput} placeholder="Email" placeholderTextColor={colors.textLight}
                value={createEmail} onChangeText={setCreateEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
              <Text style={styles.fieldLabel}>Password *</Text>
              <TextInput style={styles.fieldInput} placeholder="Password" placeholderTextColor={colors.textLight}
                value={createPassword} onChangeText={setCreatePassword} secureTextEntry maxLength={128} />
              <Text style={styles.fieldLabel}>Phone</Text>
              <TextInput style={styles.fieldInput} placeholder="Phone (optional)" placeholderTextColor={colors.textLight}
                value={createPhone} onChangeText={setCreatePhone} keyboardType="phone-pad" maxLength={20} />
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowCreate(false)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleCreate} disabled={creating}>
                <Text style={styles.saveText}>{creating ? 'Creating...' : 'Create'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        visible={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onSubmit={handleResetPassword}
        targetName={resetTarget?.businessName || ''}
      />
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  createBtn: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', gap: 4, backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, marginBottom: spacing.sm },
  createText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 10 },
  avatar: { width: rs(36), height: rs(36), borderRadius: rs(18), justifyContent: 'center', alignItems: 'center' },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  date: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  stageRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  rowActions: { flexDirection: 'row', gap: 12 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
